import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

/**
 * Reusable confirmation dialog.
 *
 * Props:
 *  - open        : boolean — whether the dialog is visible
 *  - title       : string  — heading text
 *  - message     : string/node — intro body text (rendered as a paragraph)
 *  - content     : node    — optional rich content (tables, lists) rendered
 *                            below the message; use for impact previews
 *  - confirmText : string  — confirm button label (default "Confirm")
 *  - cancelText  : string  — cancel button label (default "Cancel")
 *  - confirmColor: MUI color for the confirm button (default "primary")
 *  - confirmDisabled: boolean — disable the confirm button
 *  - maxWidth    : Dialog maxWidth (default "xs")
 *  - loading     : boolean — shows a spinner on confirm + locks the dialog
 *  - onConfirm   : () => void — called when confirm is clicked
 *  - onClose     : () => void — called when cancelled / dismissed
 */
const ConfirmDialog = ({
  open,
  title = "Confirm",
  message,
  content,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmColor = "primary",
  confirmDisabled = false,
  maxWidth = "xs",
  loading = false,
  onConfirm,
  onClose,
}) => {
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth={maxWidth}
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {message ? <DialogContentText>{message}</DialogContentText> : null}
        {content}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          {cancelText}
        </Button>
        <LoadingButton
          onClick={onConfirm}
          loading={loading}
          disabled={confirmDisabled}
          variant="contained"
          color={confirmColor}
        >
          {confirmText}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
